import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ProductModel, ProductState } from "../../components/models/product/product";

const initialState: ProductState = {
    items: [],
    selected: null,
    quantity: 1,
    status: 'idle',
    error: '',
};

const productSlice = createSlice({
    name: "product",
    initialState,
    reducers: {
        productsLoading: (state) => {
            state.status = 'loading';
            state.error = '';
        },
        setProducts: (state, action: PayloadAction<ProductModel[]>) => {
            state.items = action.payload;
            state.status = 'ready';
            state.error = '';

            // Keep the selection in sync with the freshest catalogue data,
            // and auto-select when the shop carries a single product.
            if (action.payload.length === 1) {
                state.selected = action.payload[0];
            } else if (state.selected) {
                state.selected = action.payload.find((p) => p.id === state.selected!.id) || null;
            }
        },
        productsFailed: (state, action: PayloadAction<string>) => {
            state.status = 'error';
            state.error = action.payload;
        },
        /** Admin created or edited a product — slot it into the catalogue. */
        upsertProduct: (state, action: PayloadAction<ProductModel>) => {
            const index = state.items.findIndex((p) => p.id === action.payload.id);
            if (index === -1) {
                state.items.push(action.payload);
            } else {
                state.items[index] = action.payload;
            }
            if (state.selected?.id === action.payload.id) {
                state.selected = action.payload;
            }
        },
        /** Admin deleted a product — drop it, and any selection pointing at it. */
        removeProduct: (state, action: PayloadAction<number>) => {
            state.items = state.items.filter((p) => p.id !== action.payload);
            if (state.selected?.id === action.payload) {
                state.selected = null;
                state.quantity = 1;
            }
        },
        /** Select the product being viewed / bought. */
        setProduct: (state, action: PayloadAction<ProductModel | null>) => {
            state.selected = action.payload;
        },
        setQuantityCheckout: (state, action: PayloadAction<number>) => {
            state.quantity = Math.max(1, Number(action.payload) || 1);
        },
        clearSelection: (state) => {
            state.selected = null;
            state.quantity = 1;
        },
    },
});

export const {
    productsLoading,
    setProducts,
    productsFailed,
    upsertProduct,
    removeProduct,
    setProduct,
    setQuantityCheckout,
    clearSelection,
} = productSlice.actions;

export default productSlice.reducer;
